import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, CheckCircle2, AlertTriangle, XCircle, Send, RefreshCcw } from "lucide-react";
import api, { formatErr, fmtDay } from "../../lib/api";
import { Loading, PageHeader } from "../../components/Shared";
import { useAuth } from "../../context/AuthContext";

const TOGGLES = [
  { key: "family_confirmations", label: "Family confirmations", desc: "WhatsApp confirmation to the family when a service request is created" },
  { key: "ops_notifications", label: "Ops notifications", desc: "New-request alerts to Ops Manager / designated ops recipient" },
  { key: "cm_notifications", label: "Care Manager notifications", desc: "New-request alerts to the assigned Care Manager" },
  { key: "status_updates", label: "Service status updates", desc: "WhatsApp to the family when a request status changes" },
  { key: "sos_alerts", label: "SOS alerts", desc: "Emergency WhatsApp alerts to staff (SMS + email always fire for SOS)" },
];

const STATE = {
  connected: { label: "Connected — LIVE", cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
  test_mode: { label: "Configured — TEST mode (sends simulated)", cls: "bg-amber-100 text-amber-800", Icon: AlertTriangle },
  not_configured: { label: "Not Configured", cls: "bg-stone-200 text-stone-600", Icon: XCircle },
  error: { label: "Error", cls: "bg-red-100 text-red-700", Icon: XCircle },
};

function Toggle({ on, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} role="switch" aria-checked={on}
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${on ? "bg-emerald-700" : "bg-stone-300"}`}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  );
}

export default function WhatsAppSettings() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [staff, setStaff] = useState([]);
  const [logs, setLogs] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/whatsapp/status").then((r) => setStatus(r.data)).catch((e) => { toast.error(formatErr(e)); setStatus({ error: true }); });
    api.get("/admin/whatsapp/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {});
    api.get("/admin/whatsapp/logs").then((r) => setLogs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (user?.role !== "super_admin") {
    return <div className="p1-card p-8 text-center text-sm text-stone-500" data-testid="wa-settings-denied">WhatsApp integration settings are restricted to the Super Admin.</div>;
  }
  if (!status || !settings) return <Loading />;

  const st = STATE[status.integration] || STATE.not_configured;
  const save = async (patch) => {
    try {
      const r = await api.put("/admin/whatsapp/settings", patch);
      setSettings(r.data);
      toast.success("WhatsApp settings saved");
    } catch (e) { toast.error(formatErr(e)); }
  };
  const sendTest = async () => {
    if (!testTo) { toast.error("Choose a staff recipient"); return; }
    setBusy(true); setTestResult(null);
    try {
      const r = await api.post("/admin/whatsapp/test", { user_id: testTo });
      setTestResult(r.data);
      if (r.data.ok) toast.success("Test message accepted"); else toast.error("Test message failed — see details");
      api.get("/admin/whatsapp/logs").then((r2) => setLogs(r2.data)).catch(() => {});
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="wa-settings-page">
      <PageHeader title="WhatsApp Notifications" sub="Meta WhatsApp Business Cloud API — production delivery, templates, routing and diagnostics."
        actions={<button onClick={() => { setStatus(null); load(); }} className="p1-btn-outline !px-4 !py-2 text-xs" data-testid="wa-refresh"><RefreshCcw className="h-3.5 w-3.5" /> Refresh</button>} />

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="p1-card p-6" data-testid="wa-status-card">
          <p className="flex items-center gap-2 font-heading text-lg font-semibold text-stone-900"><MessageSquare className="h-5 w-5 text-emerald-800" /> Integration</p>
          <div className="mt-4 flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${st.cls}`} data-testid="wa-integration-state">
              <st.Icon className="h-3.5 w-3.5" /> {st.label}
            </span>
            {status.recent_failures_24h > 0 && <span className="rounded-full bg-red-100 px-2.5 py-1 text-xs font-bold text-red-700" data-testid="wa-failures-24h">{status.recent_failures_24h} failed (24h)</span>}
          </div>
          <dl className="mt-4 space-y-2.5 text-sm">
            <div className="flex justify-between"><dt className="text-stone-500">Production number</dt><dd className="font-semibold text-stone-800" data-testid="wa-number">{status.phone_masked}</dd></div>
            <div className="flex justify-between"><dt className="text-stone-500">Phone Number ID</dt><dd className="font-semibold text-stone-800" data-testid="wa-pnid">{status.phone_number_id_masked}</dd></div>
            <div className="flex justify-between"><dt className="text-stone-500">Business Account (WABA)</dt><dd className="font-semibold text-stone-800" data-testid="wa-waba">{status.waba_id_masked}</dd></div>
            <div className="flex justify-between"><dt className="text-stone-500">Graph API</dt><dd className="font-semibold text-stone-800">{status.api_version}</dd></div>
          </dl>
          {status.missing?.length > 0 && (
            <p className="mt-3 rounded-xl bg-amber-50 p-3 text-xs font-semibold text-amber-800" data-testid="wa-missing">
              Missing configuration: {status.missing.join(", ")} — add these in Emergent Secrets (Re-publish → Secrets), then set NOTIFY_TEST_MODE=false.
            </p>
          )}
          {status.api_check && !status.api_check.ok && status.configured && (
            <p className="mt-3 rounded-xl bg-red-50 p-3 text-xs font-semibold text-red-700" data-testid="wa-api-error">Meta API check: {status.api_check.detail}</p>
          )}
          {status.api_check?.ok && (
            <p className="mt-3 rounded-xl bg-emerald-50 p-3 text-xs font-semibold text-emerald-900" data-testid="wa-api-ok">
              Meta API check OK — {status.api_check.display_phone_number} ({status.api_check.verified_name || "display name pending"}) · number status: {status.api_check.number_status || "—"}
            </p>
          )}
          <p className="mt-3 text-xs text-stone-400">Access tokens are never displayed or returned by the API.</p>
        </div>

        <div className="space-y-6">
          <div className="p1-card p-6" data-testid="wa-toggles-card">
            <p className="font-heading text-lg font-semibold text-stone-900">Notification switches</p>
            <div className="mt-4 space-y-3">
              {TOGGLES.map((t) => (
                <div key={t.key} className="flex items-center justify-between rounded-xl border border-stone-100 p-3">
                  <div>
                    <p className="text-sm font-semibold text-stone-800">{t.label}</p>
                    <p className="text-xs text-stone-400">{t.desc}</p>
                  </div>
                  <Toggle on={!!settings[t.key]} onClick={() => save({ [t.key]: !settings[t.key] })} testid={`wa-toggle-${t.key}`} />
                </div>
              ))}
              <div className="rounded-xl border border-stone-100 p-3">
                <p className="text-sm font-semibold text-stone-800">Default Ops notification recipient</p>
                <p className="text-xs text-stone-400">Always notified on new service requests (in addition to city-routed Ops Managers).</p>
                <select className="p1-input mt-2" value={settings.default_ops_recipient || ""} onChange={(e) => save({ default_ops_recipient: e.target.value })} data-testid="wa-default-ops">
                  <option value="">— All Ops Managers —</option>
                  {staff.filter((s) => ["ops_manager", "admin"].includes(s.role)).map((s) => (
                    <option key={s.id} value={s.id}>{s.name} · {s.role.replace(/_/g, " ")} · {s.phone || "no mobile"}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="p1-card p-6" data-testid="wa-test-card">
            <p className="font-heading text-lg font-semibold text-stone-900">Test WhatsApp integration</p>
            <p className="mt-1 text-xs text-stone-500">Sends the approved ops-alert template through the registered ParentOne number to a staff member.</p>
            <div className="mt-3 flex gap-2">
              <select className="p1-input flex-1" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="wa-test-recipient">
                <option value="">— Select staff recipient —</option>
                {staff.filter((s) => s.phone).map((s) => <option key={s.id} value={s.id}>{s.name} · {s.phone}</option>)}
              </select>
              <button onClick={sendTest} disabled={busy} className="p1-btn-primary !px-4 !py-2 text-xs" data-testid="wa-test-send"><Send className="h-3.5 w-3.5" /> {busy ? "Sending…" : "Send test"}</button>
            </div>
            {testResult && (
              <div className={`mt-3 rounded-xl p-3 text-xs ${testResult.ok ? "bg-emerald-50 text-emerald-900" : "bg-red-50 text-red-700"}`} data-testid="wa-test-result">
                <p className="font-bold">Status: {testResult.result?.status}{testResult.result?.id ? ` · wamid: ${testResult.result.id}` : ""}</p>
                {testResult.note && <p className="mt-1">{testResult.note}</p>}
                {testResult.result?.detail && <p className="mt-1">Meta response: {testResult.result.detail}</p>}
                {testResult.result?.failure_code && <p className="mt-1">Failure code: {testResult.result.failure_code}</p>}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p1-card mt-6 p-6" data-testid="wa-templates-card">
        <p className="font-heading text-lg font-semibold text-stone-900">Required approved templates</p>
        <p className="mt-1 text-sm text-stone-500">Create these as UTILITY templates (language en_US, positional body parameters) in WhatsApp Manager and wait for Meta approval. Names can be remapped via the WHATSAPP_TEMPLATES environment variable.</p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm" data-testid="wa-templates-table">
            <thead className="bg-stone-50 text-left text-xs uppercase tracking-wider text-stone-500">
              <tr><th className="p-2.5">Template name</th><th className="p-2.5">Parameters</th><th className="p-2.5">Used for</th></tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {(status.templates_required || []).map((t) => (
                <tr key={t.key} data-testid={`wa-template-${t.key}`}>
                  <td className="p-2.5 font-mono text-xs font-bold text-emerald-900">{t.name}</td>
                  <td className="p-2.5 text-xs text-stone-500">{t.params.map((p, i) => `{{${i + 1}}} ${p}`).join(" · ")}</td>
                  <td className="p-2.5 text-xs text-stone-600">{t.used_for}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="p1-card mt-6 p-6" data-testid="wa-logs-card">
        <p className="font-heading text-lg font-semibold text-stone-900">Recent WhatsApp delivery log</p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[640px] text-xs" data-testid="wa-logs-table">
            <thead className="bg-stone-50 text-left uppercase tracking-wider text-stone-500">
              <tr><th className="p-2">Time</th><th className="p-2">Template</th><th className="p-2">Recipient</th><th className="p-2">Phone</th><th className="p-2">Status</th><th className="p-2">Failure</th></tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {logs.slice(0, 25).map((l) => (
                <tr key={l.id} data-testid={`wa-log-${l.id}`}>
                  <td className="p-2 text-stone-400">{fmtDay(l.created_at)}</td>
                  <td className="p-2 font-mono text-stone-700">{l.template_name}</td>
                  <td className="p-2 capitalize text-stone-600">{l.recipient_type}</td>
                  <td className="p-2 text-stone-600">{l.recipient_phone_masked}</td>
                  <td className="p-2 font-semibold text-stone-700">{l.status}{l.test_mode ? " (test)" : ""}</td>
                  <td className="p-2 text-red-600">{l.failure_code ? `${l.failure_code}: ${(l.failure_reason || "").slice(0, 60)}` : ""}</td>
                </tr>
              ))}
              {logs.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-stone-400">No WhatsApp attempts logged yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
